import React from 'react';
import { motion } from 'framer-motion';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Clock, User, Calendar, ArrowRight } from 'lucide-react';
import { mockData } from '../data/mock';
import { blogContent } from '../data/blogContent';

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  } catch {
    return iso;
  }
};

const BlogPost = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const postId = parseInt(id, 10);
  const post = mockData.blog.find((p) => p.id === postId);
  const content = blogContent[postId] || [];

  if (!post) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-6 text-center">
        <h1 className="text-4xl font-bold mb-4" style={{ color: '#1A1A1A' }}>Article Not Found</h1>
        <p className="text-gray-600 mb-8">The article you're looking for doesn't exist.</p>
        <Link to="/blog">
          <button className="btn-premium px-8 py-4 rounded-full text-white font-semibold" style={{ background: '#7CB8D6' }}>
            Back to Blog
          </button>
        </Link>
      </div>
    );
  }

  // Related posts (same category, excluding current)
  const related = mockData.blog
    .filter((p) => p.category === post.category && p.id !== post.id)
    .slice(0, 2);

  return (
    <div className="min-h-screen bg-white">
      {/* Hero */}
      <section className="pt-32 pb-12 px-6" style={{ background: 'linear-gradient(180deg, #A9C9D9 0%, #FFFFFF 100%)' }}>
        <div className="max-w-3xl mx-auto">
          <button
            onClick={() => navigate('/blog')}
            data-testid="back-to-blog"
            className="flex items-center gap-2 text-sm font-semibold mb-8 hover:opacity-70 transition-opacity"
            style={{ color: '#1A1A1A' }}
          >
            <ArrowLeft size={18} /> Back to Blog
          </button>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-xs font-semibold px-3 py-1 rounded-full" style={{ background: 'rgba(124, 184, 214, 0.15)', color: '#5a94b3' }}>
              {post.category}
            </span>
            <h1 className="text-3xl md:text-5xl font-bold mt-6 mb-6 leading-tight" style={{ color: '#1A1A1A' }}>
              {post.title}
            </h1>
            <div className="flex flex-wrap items-center gap-6 text-sm text-gray-600">
              <span className="flex items-center gap-2"><User size={16} /> {post.author}</span>
              <span className="flex items-center gap-2"><Calendar size={16} /> {formatDate(post.date)}</span>
              <span className="flex items-center gap-2"><Clock size={16} /> {post.readTime}</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Featured image */}
      <div className="max-w-4xl mx-auto px-6 -mt-2">
        <motion.div
          className="rounded-3xl overflow-hidden shadow-xl aspect-video"
          initial={{ opacity: 0, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, delay: 0.1 }}
        >
          <img src={post.image} alt={post.title} className="w-full h-full object-cover" />
        </motion.div>
      </div>

      {/* Article body */}
      <article className="max-w-3xl mx-auto px-6 py-16">
        <div className="space-y-6">
          {content.map((block, i) => {
            if (block.type === 'h2') {
              return (
                <h2 key={i} className="text-2xl md:text-3xl font-bold pt-6" style={{ color: '#1A1A1A' }}>
                  {block.text}
                </h2>
              );
            }
            if (block.type === 'quote') {
              return (
                <blockquote key={i} className="border-l-4 pl-6 py-2 my-8 text-xl md:text-2xl font-medium italic" style={{ borderColor: '#F4C430', color: '#374151' }}>
                  {block.text}
                </blockquote>
              );
            }
            if (block.type === 'list') {
              return (
                <ul key={i} className="space-y-3 my-4">
                  {block.items.map((item, j) => (
                    <li key={j} className="flex items-start gap-3 text-lg text-gray-700 leading-relaxed">
                      <span className="mt-2.5 w-2 h-2 rounded-full flex-shrink-0" style={{ background: '#7CB8D6' }} />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              );
            }
            return (
              <p key={i} className="text-lg text-gray-700 leading-relaxed">
                {block.text}
              </p>
            );
          })}
        </div>

        {/* Author footer */}
        <div className="mt-16 pt-8 border-t border-gray-200 flex items-center gap-4">
          <div className="w-14 h-14 rounded-full flex items-center justify-center text-lg font-bold flex-shrink-0" style={{ background: 'rgba(124, 184, 214, 0.15)', color: '#5a94b3' }}>
            {post.author.split(' ').map((n) => n[0]).join('')}
          </div>
          <div>
            <div className="font-semibold" style={{ color: '#1A1A1A' }}>Written by {post.author}</div>
            <div className="text-sm text-gray-500">CLIMATECO2RE Insights</div>
          </div>
        </div>
      </article>

      {/* Related posts */}
      {related.length > 0 && (
        <section className="px-6 pb-24">
          <div className="max-w-4xl mx-auto">
            <h3 className="text-2xl font-bold mb-8" style={{ color: '#1A1A1A' }}>Related Articles</h3>
            <div className="grid md:grid-cols-2 gap-8">
              {related.map((rp) => (
                <Link to={`/blog/${rp.id}`} key={rp.id} className="group">
                  <div className="card-premium rounded-3xl bg-white border-2 border-gray-100 overflow-hidden">
                    <div className="aspect-video overflow-hidden">
                      <img src={rp.image} alt={rp.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    </div>
                    <div className="p-6">
                      <span className="text-xs font-semibold" style={{ color: '#7CB8D6' }}>{rp.category}</span>
                      <h4 className="text-lg font-bold mt-2 mb-3 line-clamp-2" style={{ color: '#1A1A1A' }}>{rp.title}</h4>
                      <span className="flex items-center gap-1 text-sm font-semibold" style={{ color: '#7CB8D6' }}>
                        Read More <ArrowRight size={16} />
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-20 px-6" style={{ background: 'linear-gradient(135deg, #7CB8D6 0%, #A9C9D9 100%)' }}>
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white">Ready to see it in action?</h2>
          <p className="text-lg text-white/90 mb-8">Discover how CLIMATECO2RE brings transparency and intelligence to your ESG strategy.</p>
          <Link to="/contact">
            <button className="btn-premium px-10 py-5 rounded-full text-lg font-semibold" style={{ background: '#F4D740', color: '#1A1A1A' }}>
              Request a Demo
            </button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default BlogPost;
